/**
 * useChart Hook
 * ==============
 * React Query hooks for chart operations.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { useChartStore } from '@/stores/chartStore';
import { useOfflineStore } from '@/stores/offlineStore';
import * as chartsApi from '@/api/charts';
import type { Chart, ChartBrief, ToothData } from '@/types';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const chartKeys = {
  all: ['charts'] as const,
  lists: () => [...chartKeys.all, 'list'] as const,
  list: (filters: chartsApi.ChartSearchParams | Record<string, unknown>) => [...chartKeys.lists(), filters] as const,
  details: () => [...chartKeys.all, 'detail'] as const,
  detail: (id: string) => [...chartKeys.details(), id] as const,
  patient: (patientId: string) => [...chartKeys.all, 'patient', patientId] as const,
  comparison: (currentId: string, baselineId: string) =>
    [...chartKeys.all, 'comparison', currentId, baselineId] as const,
};

// =============================================================================
// QUERIES
// =============================================================================

/**
 * List charts with filters.
 */
export function useCharts(params: chartsApi.ChartSearchParams = {}) {
  return useQuery({
    queryKey: chartKeys.list(params),
    queryFn: () => chartsApi.listCharts(params),
  });
}

/**
 * Get patient's charts.
 */
export function usePatientCharts(patientId: string | null, includeDrafts = true) {
  return useQuery({
    queryKey: chartKeys.patient(patientId || ''),
    queryFn: () => chartsApi.getPatientCharts(patientId!, includeDrafts),
    enabled: !!patientId,
  });
}

/**
 * Get chart by ID.
 */
export function useChart(chartId: string | null) {
  const { cacheChart } = useOfflineStore();

  return useQuery({
    queryKey: chartKeys.detail(chartId || ''),
    queryFn: async () => {
      const chart = await chartsApi.getChart(chartId!);
      // Cache for offline use
      cacheChart(chart);
      return chart;
    },
    enabled: !!chartId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Compare two charts.
 */
export function useChartComparison(currentId: string | null, baselineId: string | null) {
  return useQuery({
    queryKey: chartKeys.comparison(currentId || '', baselineId || ''),
    queryFn: () => chartsApi.compareCharts(currentId!, baselineId!),
    enabled: !!currentId && !!baselineId,
  });
}

// =============================================================================
// MUTATIONS
// =============================================================================

/**
 * Create chart mutation.
 */
export function useCreateChart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: chartsApi.ChartCreateRequest) => chartsApi.createChart(data),
    onSuccess: (chart) => {
      queryClient.invalidateQueries({ queryKey: chartKeys.patient(chart.patient_id) });
      queryClient.invalidateQueries({ queryKey: chartKeys.lists() });
      toast.success('Chart created');
      return chart;
    },
    onError: (error: any) => {
      const message = error.response?.data?.error?.message || 'Failed to create chart';
      toast.error(message);
    },
  });
}

/**
 * Update chart mutation.
 */
export function useUpdateChart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ chartId, data }: { chartId: string; data: chartsApi.ChartUpdateRequest }) =>
      chartsApi.updateChart(chartId, data),
    onSuccess: (chart) => {
      queryClient.setQueryData(chartKeys.detail(chart.id), chart);
      queryClient.invalidateQueries({ queryKey: chartKeys.patient(chart.patient_id) });
    },
    onError: (error: any) => {
      const message = error.response?.data?.error?.message || 'Failed to update chart';
      toast.error(message);
    },
  });
}

/**
 * Finalize chart mutation.
 */
export function useFinalizeChart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ chartId, data }: { chartId: string; data: chartsApi.ChartFinalizeRequest }) =>
      chartsApi.finalizeChart(chartId, data),
    onSuccess: (chart) => {
      queryClient.setQueryData(chartKeys.detail(chart.id), chart);
      queryClient.invalidateQueries({ queryKey: chartKeys.patient(chart.patient_id) });
      queryClient.invalidateQueries({ queryKey: chartKeys.lists() });
      toast.success('Chart finalized successfully');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error?.message || 'Failed to finalize chart';
      toast.error(message);
    },
  });
}

/**
 * Delete chart mutation.
 */
export function useDeleteChart() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chartId: string) => chartsApi.deleteChart(chartId),
    onSuccess: (_, chartId) => {
      queryClient.invalidateQueries({ queryKey: chartKeys.lists() });
      queryClient.removeQueries({ queryKey: chartKeys.detail(chartId) });
      toast.success('Chart deleted');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error?.message || 'Failed to delete chart';
      toast.error(message);
    },
  });
}

/**
 * Export chart mutation.
 */
export function useExportChart() {
  return useMutation({
    mutationFn: (data: chartsApi.ChartExportRequest) => chartsApi.exportChart(data),
    onSuccess: (response) => {
      // Open download URL in new tab
      window.open(response.download_url, '_blank');
      toast.success('Export ready');
    },
    onError: (error: any) => {
      const message = error.response?.data?.error?.message || 'Failed to export chart';
      toast.error(message);
    },
  });
}

// =============================================================================
// AUTOSAVE HOOK
// =============================================================================

/**
 * Hook for managing autosave functionality.
 */
export function useChartAutosave(
  chartId: string | null,
  enabled = true,
  intervalMs = 30000
) {
  const { chart, isDirty, triggerAutosave } = useChartStore();
  const { isOnline, addPendingChange } = useOfflineStore();
  const autosaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastSaveRef = useRef<number>(0);

  const doAutosave = useCallback(async () => {
    if (!chart || !isDirty || !chartId) return;

    const now = Date.now();
    // Debounce: don't save more than once per 5 seconds
    if (now - lastSaveRef.current < 5000) return;

    lastSaveRef.current = now;

    if (isOnline) {
      try {
        await triggerAutosave();
      } catch (error) {
        console.error('Autosave failed:', error);
        // Queue for offline sync
        addPendingChange({
          type: 'chart_update',
          entityId: chartId,
          data: {
            chartId,
            updates: {
              tooth_data: chart.tooth_data,
              clinical_notes: chart.clinical_notes,
            },
          },
        });
      }
    } else {
      // Queue for offline sync
      addPendingChange({
        type: 'chart_update',
        entityId: chartId,
        data: {
          chartId,
          updates: {
            tooth_data: chart.tooth_data,
            clinical_notes: chart.clinical_notes,
          },
        },
      });
    }
  }, [chart, isDirty, chartId, isOnline, triggerAutosave, addPendingChange]);

  // Set up autosave interval
  useEffect(() => {
    if (!enabled || !chartId) return;

    autosaveTimerRef.current = setInterval(doAutosave, intervalMs);

    return () => {
      if (autosaveTimerRef.current) {
        clearInterval(autosaveTimerRef.current);
      }
    };
  }, [enabled, chartId, intervalMs, doAutosave]);

  // Save on blur (when user leaves the tab)
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        doAutosave();
      }
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        doAutosave();
        e.preventDefault();
        e.returnValue = '';
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, isDirty, doAutosave]);

  return {
    triggerSave: doAutosave,
  };
}

// =============================================================================
// CHART LOCKING HOOK
// =============================================================================

/**
 * Hook for managing chart edit locks.
 */
export function useChartLock(chartId: string | null) {
  const lockRenewalRef = useRef<NodeJS.Timeout | null>(null);

  const acquireLock = useMutation({
    mutationFn: () => {
      if (!chartId) throw new Error('No chart ID');
      return chartsApi.acquireChartLock(chartId);
    },
    onSuccess: (response) => {
      if (response.locked && response.locked_until) {
        // Set up lock renewal before it expires
        const expiresAt = new Date(response.locked_until).getTime();
        const renewIn = Math.max(0, expiresAt - Date.now() - 60000); // 1 minute before expiry
        
        lockRenewalRef.current = setTimeout(() => {
          acquireLock.mutate();
        }, renewIn);
      }
    },
    onError: (error: any) => {
      const message = error.response?.data?.error?.message || 'Failed to acquire lock';
      toast.error(message);
    },
  });

  const releaseLock = useMutation({
    mutationFn: () => {
      if (!chartId) throw new Error('No chart ID');
      return chartsApi.releaseChartLock(chartId);
    },
    onSuccess: () => {
      if (lockRenewalRef.current) {
        clearTimeout(lockRenewalRef.current);
        lockRenewalRef.current = null;
      }
    },
  });

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (lockRenewalRef.current) {
        clearTimeout(lockRenewalRef.current);
      }
      // Try to release lock on unmount
      if (chartId) {
        chartsApi.releaseChartLock(chartId).catch(() => {});
      }
    };
  }, [chartId]);

  return {
    acquireLock: acquireLock.mutate,
    releaseLock: releaseLock.mutate,
    isAcquiring: acquireLock.isPending,
    isReleasing: releaseLock.isPending,
  };
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Get the latest finalized chart for a patient.
 */
export function useLatestFinalizedChart(patientId: string | null) {
  const { data: charts } = usePatientCharts(patientId, false);
  
  if (!charts || charts.length === 0) return null;
  
  // Charts are sorted by exam_date desc, so first one is latest
  return charts[0];
}

/**
 * Check if a chart can be edited.
 */
export function useCanEditChart(chart: Chart | null): boolean {
  if (!chart) return false;
  return chart.status === 'draft';
}

/**
 * Get baseline chart for comparison.
 */
export function useBaselineChart(patientId: string | null) {
  const { data: charts } = usePatientCharts(patientId, false);
  
  if (!charts) return null;
  
  // Find the baseline chart
  return charts.find((c) => c.is_baseline) || null;
}
